/** A read-only line for an RTP-moving lever that lives on another tab. */
export interface RtpContributor {
  /** Display label, e.g. "Jackpot contribution". */
  label: string;
  /** Tab id to deep-link to. */
  tab: 'jackpot' | 'category-bonus' | 'streak';
  /** Short note on how it moves RTP. */
  note: string;
}

/**
 * Per-game shape of the unified RTP tab. Pure data — no DOM. `hasSolver` marks
 * a one-number Target-RTP solver. sicbo/dragontiger use the closed-form affine
 * bps solver; greedyking uses a numeric (simulate-based) solver that scales all
 * food multipliers to a target OVERALL RTP — both flow through the same panel +
 * endpoint. `solverKind` lets the panel tailor its copy.
 */
export interface RtpSurfaceDescriptor {
  gameId: string;
  hasWeights: boolean;
  hasMultipliers: boolean;
  hasSolver: boolean;
  /** How the solver targets RTP: per-bet (affine) or overall (numeric scale).
   *  null when there is no solver. Drives the panel's copy. */
  solverKind: 'per-bet' | 'overall' | null;
  contributors: RtpContributor[];
}

const DEFAULT_DESCRIPTOR: Omit<RtpSurfaceDescriptor, 'gameId'> = {
  hasWeights: true,
  hasMultipliers: true,
  hasSolver: false,
  solverKind: null,
  contributors: [],
};

const BY_GAME: Record<string, Omit<RtpSurfaceDescriptor, 'gameId'>> = {
  // Mines Chest has no weight table: one seeded draw picks which ten of the
  // twenty chests are explosive, and every layout is equally likely. Its
  // `multipliers` surface carries two settings and no payout figure: the RTP
  // constant `rtpBps`, from which the payout for each number of chests is
  // computed, and `maxPicks`, the most chests one bet may hold.
  //
  // `hasSolver: false` is deliberate. The RTP can be reported (the game is in
  // admin-api's RTP_REPORTABLE_GAMES, so the headline shows a real figure), but
  // there is nothing to invert: the field an admin edits already is what a
  // solver would return. No jackpot, bonus or streak moves its RTP, so there
  // are no contributors.
  mineschest: {
    hasWeights: false,
    hasMultipliers: true,
    hasSolver: false,
    solverKind: null,
    contributors: [],
  } /* apx-cli:rtp:mineschest */,
  // Crash has no weight table: the outcome is a single multiplier derived from
  // the seed, not a pick from a weighted set. Its `multipliers` surface carries
  // exactly one field, `rtpBps` — which IS the RTP, because `P(reach x) = k/x`
  // and a win pays `x`, so the expected return is `k` at every target.
  //
  // `hasSolver: false` is deliberate and is NOT the #427 mistake. That was a
  // game whose RTP could be reported but not inverted. Here there is nothing to
  // invert: the field an admin edits already IS what a solver would return, so
  // a solver would be a button that computes `k = target`. Crash is in
  // RTP_REPORTABLE_GAMES so the headline renders a real figure.
  crash: {
    hasWeights: false,
    hasMultipliers: true,
    hasSolver: false,
    solverKind: null,
    contributors: [],
  },
  sicbo: {
    hasWeights: true,
    hasMultipliers: true,
    hasSolver: true,
    solverKind: 'per-bet',
    contributors: [
      { label: 'Jackpot contribution', tab: 'jackpot', note: 'A jackpot rake lowers base RTP.' },
    ],
  },
  dragontiger: {
    hasWeights: true,
    hasMultipliers: true,
    hasSolver: true,
    solverKind: 'per-bet',
    contributors: [],
  },
  greedyking: {
    hasWeights: true,
    hasMultipliers: true,
    hasSolver: true,
    solverKind: 'overall',
    contributors: [
      { label: 'Jackpot contribution', tab: 'jackpot', note: 'Pool rake lowers base RTP.' },
      { label: 'Category bonus', tab: 'category-bonus', note: 'Extra payouts raise RTP.' },
      { label: 'Streak bonus', tab: 'streak', note: 'Streak rewards raise RTP.' },
    ],
  },
  // Flat bps paytable, no RNG weight surface (digits are a fixed uniform 1/10 per
  // position) and no bps target-RTP solver (it is absent from admin-api's
  // RTP_SOLVABLE_GAMES). The tunable surface is the payout multipliers: the 1.90
  // Big/Small/Odd/Even cells and the 9.50 exact-first-digit (0–9) cells.
  singaporelottery: {
    hasWeights: false,
    hasMultipliers: true,
    hasSolver: false,
    solverKind: null,
    contributors: [],
  },
  // Fixed uniform card draw + fixed drawing rules, so there is no RNG weight
  // surface. Tunable RTP comes only from the payout multipliers (tie 8:1 vs
  // 9:1, pair odds, and the banker commission baked into its 1.95 return).
  // Each cell's RTP is affine in its own multiplier, so the per-bet bps solver
  // inverts cleanly — no weights to interact with and no crit cap to clip it.
  // OPERATOR NOTE: solving rewrites the standard 1:1 / 0.95:1 / 8:1 / 11:1
  // odds into non-standard numbers, which players recognise. Prefer editing one
  // multiplier and reading the exact recomputed RTP; use the solver only when a
  // specific house edge matters more than a conventional-looking table.
  // Both Dice Rush tables: three fair dice, uniform and NOT admin-tunable, so
  // there is no RNG weight surface at all. RTP is tuned purely through the
  // sixteen payout multipliers, and because each area's win probability is a
  // constant, its RTP is affine in its own multiplier — the per-bet bps solver
  // inverts it exactly, with no weights to interact with.
  //
  // OPERATOR NOTE: solving rewrites the round 1.92 / 2.28 / 12.96 figures into
  // numbers players do not recognise, AND a rate whose gcd with 10000 drops
  // below 400 takes the chip ladder off the 25-credit stake grid, where every
  // win is floored down. Prefer editing one multiplier and reading the exact
  // recomputed RTP; use the solver only when a specific house edge matters more.
  shortdicerush: {
    hasWeights: false,
    hasMultipliers: true,
    hasSolver: true,
    solverKind: 'per-bet',
    contributors: [],
  },
  longdicerush: {
    hasWeights: false,
    hasMultipliers: true,
    hasSolver: true,
    solverKind: 'per-bet',
    contributors: [],
  },
  baccarat: {
    hasWeights: false,
    hasMultipliers: true,
    hasSolver: true,
    solverKind: 'per-bet',
    contributors: [],
  },
  // Wingo: one number from 0 to 9, drawn uniformly and NOT admin-tunable, so
  // there is no RNG weight surface at all. RTP is tuned purely through the
  // thirteen payout multipliers, and because each spot wins on a fixed count of
  // the ten numbers, its RTP is affine in its own multiplier — the per-bet bps
  // solver inverts it exactly, with no weights to interact with.
  //
  // OPERATOR NOTE: every rate must be a multiple of 200 bps, or the chip ladder
  // comes off the 50-credit stake grid and every win is floored down. The
  // solver therefore rounds DOWN to that step, and the step is coarse: one step
  // is a full point of RTP on Green or Red, 0.4 on Purple and 0.2 on a number,
  // so a target between steps lands under it. Solving also rewrites the round
  // 1.96 / 4.90 / 9.80 figures players recognise. Prefer editing one multiplier
  // and reading the exact recomputed RTP.
  wingo: {
    hasWeights: false,
    hasMultipliers: true,
    hasSolver: true,
    solverKind: 'per-bet',
    contributors: [],
  },
  // Fish Prawn Crab: three symbol dice, uniform and NOT admin-tunable, so there
  // is no RNG weight surface at all. RTP is tuned purely through ONE figure per
  // bet family: the Three Soldiers per-die winnings, the Pair return and the
  // Long Card return. Every symbol is equally likely, so the six (or fifteen)
  // bets of a family share that figure, and each family's RTP is affine in it —
  // the per-bet bps solver inverts it exactly, with no weights to interact with.
  //
  // OPERATOR NOTE: every figure must be a multiple of 100 bps (0.01x), or the
  // chip ladder comes off the 100-credit stake grid and every win is floored
  // down. The solver therefore rounds DOWN to that step: one step moves RTP
  // 0.50 points on Three Soldiers, 0.07 on a Pair and 0.14 on Long Card, so a
  // target between steps lands under it — a 96% target gives 1.07 a die on
  // Three Soldiers, which is 95.63%. Prefer editing one figure and reading the
  // exact recomputed RTP.
  fishprawncrab: {
    hasWeights: false,
    hasMultipliers: true,
    hasSolver: true,
    solverKind: 'per-bet',
    contributors: [],
  },
  // Space Hero has no weight table: the outcome is one multiplier derived from
  // the seed, not a pick from a weighted set. Its `multipliers` surface carries
  // exactly one field, `rtpBps`, and that field IS the RTP: `P(reach x) = k / x`
  // and a win returns `x`, so the expected return is `k` at every target.
  //
  // `hasSolver: false` is deliberate. The RTP can be reported (the game is in
  // admin-api's RTP_REPORTABLE_GAMES, so the headline shows a real figure), but
  // there is nothing to invert: the field an admin edits already is what a
  // solver would return.
  spacehero: {
    hasWeights: false,
    hasMultipliers: true,
    hasSolver: false,
    solverKind: null,
    contributors: [],
  },
  // Animal Party: one weighted draw of ten results. The weight table is a
  // versioned constant — changing it is a code change, a version bump and a new
  // evidence run, never an admin edit — so `hasWeights` is false and there is
  // no RNG weights subtab. RTP is tuned through the eleven payout multipliers,
  // and each spot's RTP is P(win) x multiple, affine in its own multiple, so
  // the per-bet solver inverts it.
  //
  // OPERATOR NOTE: every multiple moves on a fixed bps step (`RETURN_BPS_STEP`
  // in the game package), which is what keeps the chip ladder on the stake
  // grid. The solver rounds DOWN to that step, so a spot can land visibly under
  // its target — most of all a group paying a small multiple, where one step is
  // several points of RTP. Solving also rewrites the whole-number multiples
  // players see on the ring. Prefer editing one multiple and reading the exact
  // recomputed RTP.
  animalparty: {
    hasWeights: false,
    hasMultipliers: true,
    hasSolver: true,
    solverKind: 'per-bet',
    contributors: [],
  },
};

/**
 * Whether a game has its OWN entry above rather than the fallback. The fallback
 * says "has RNG weights, has multipliers, no solver", which is wrong for most
 * tables, so a game left on it renders another game's editors. Exported for the
 * registry guard in `__tests__/game-registries.test.ts`.
 */
export function hasOwnDescriptor(gameId: string): boolean {
  return Object.prototype.hasOwnProperty.call(BY_GAME, gameId);
}

export function descriptorFor(gameId: string): RtpSurfaceDescriptor {
  const base = BY_GAME[gameId] ?? DEFAULT_DESCRIPTOR;
  return { gameId, ...base };
}

/** Map retired tab ids (`rng`, `multipliers`) onto the unified `rtp` tab. */
export function aliasRtpTab<T extends string | null>(tab: T): T | 'rtp' {
  if (tab === 'rng' || tab === 'multipliers') return 'rtp';
  return tab;
}
